/**
 * PanelSection — seção colapsável canônica do painel direito.
 *
 * Espelha o `.section` do Brief 2A r7a/r7b (standalone.html): head clicável
 * com chevron rotativo + título + subtítulo mono (small) + count circular,
 * e body que abre/fecha. Classes DS `.section*` (components.css — migração
 * verbatim ADR-141 Caso B).
 */

import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

import type { KeyboardEvent, ReactNode } from 'react';

export function PanelSection({
  title,
  eyebrow,
  count,
  defaultOpen = true,
  headerAction,
  children,
}: {
  title: string;
  /** Subtítulo mono exibido sob o título (small do `.section__title`). */
  eyebrow?: string;
  count?: number;
  defaultOpen?: boolean;
  /** ADR-155 Fase 4 (§3.1) — slot de ação no HEAD (ex.: botão "Agendar" da
   *  seção "Agendamentos relacionados"). Clique não propaga pro toggle. */
  headerAction?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = (): void => setOpen((v) => !v);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <section className="section">
      <div
        className="section__head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span className={`section__chev${open ? ' is-open' : ''}`}>
          <ChevronRight className="icon" size={14} aria-hidden />
        </span>
        <div className="section__title">
          <h3>{title}</h3>
          {eyebrow !== undefined && eyebrow.length > 0 && <small>{eyebrow}</small>}
        </div>
        {count !== undefined && count > 0 && (
          <span className="section__count" data-testid="panel-section-count">
            {count}
          </span>
        )}
        {/* Nota: o próprio elemento de `headerAction` (um <button>) deve
            chamar `e.stopPropagation()` no seu onClick — senão o clique
            também alterna o open/close do head (bubbling). */}
        {headerAction}
      </div>
      {open && <div className="section__body">{children}</div>}
    </section>
  );
}
